import { useState, useRef, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '@/hooks/useAuth';
import { User, LogOut, ShieldCheck, Inbox, LogIn, KeyRound, BarChart2, Scale } from 'lucide-react';

const ROLE_LABEL: Record<string, { label: string; color: string }> = {
  owner: { label: 'Admin', color: 'text-amber-600 dark:text-amber-400 bg-amber-500/10' },
  produccion: { label: 'Producción', color: 'text-blue-700 dark:text-blue-400 bg-blue-500/10' },
  direccion: { label: 'Dirección', color: 'text-emerald-700 dark:text-emerald-400 bg-emerald-500/10' },
  guest: { label: 'Invitado', color: 'text-pelip-text-muted bg-slate-500/10' },
  desarrollo: { label: 'Desarrollo', color: 'text-violet-700 dark:text-violet-400 bg-violet-500/10' },
};

export default function UserMenu() {
  const { isAuthenticated, profile, user, signOut, isOwner, isStaff, loading } = useAuth();
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();
  const menuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    function onClick(e: MouseEvent) {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) setOpen(false);
    }
    if (open) document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, [open]);

  if (loading) return null;

  if (!isAuthenticated) {
    return (
      <Link
        to="/login"
        className="inline-flex items-center gap-2 px-3 py-1.5 rounded-md bg-pelip-bg-elev-2/80 hover:bg-slate-700 text-xs text-pelip-text-soft transition-colors"
      >
        <LogIn className="w-3.5 h-3.5" />
        Iniciar sesión
      </Link>
    );
  }

  const roleInfo = ROLE_LABEL[profile?.role ?? 'guest'] ?? ROLE_LABEL.guest;
  const displayName = profile?.name || profile?.email || user?.email || 'Usuario';
  const initials = displayName.split(/[ @]/).filter(Boolean).slice(0, 2).map(s => s[0]?.toUpperCase()).join('') || '?';

  return (
    <div ref={menuRef} className="relative">
      <button
        onClick={() => setOpen(!open)}
        className="inline-flex items-center gap-2 pl-1 pr-2 py-1 rounded-md bg-pelip-bg-elev hover:bg-pelip-bg-elev-2 border border-pelip-border transition-colors"
      >
        <div className="w-6 h-6 rounded-full bg-slate-700 flex items-center justify-center text-[10px] font-bold text-white">
          {initials}
        </div>
        <span className="text-[11px] text-pelip-text-soft max-w-[140px] truncate">{displayName}</span>
        <span className={`text-[9px] uppercase tracking-wider font-semibold px-1.5 py-0.5 rounded ${roleInfo.color}`}>
          {roleInfo.label}
        </span>
      </button>

      {open && (
        <div className="absolute right-0 top-full mt-2 w-60 bg-pelip-bg-elev border border-pelip-border-strong rounded-md shadow-xl overflow-hidden z-50">
          <div className="px-3 py-2 border-b border-pelip-border">
            <div className="text-xs font-medium text-pelip-text truncate">{displayName}</div>
            <div className="text-[10px] text-pelip-text-faint truncate">{user?.email}</div>
          </div>

          {isOwner && (
            <Link
              to="/admin/feedback"
              onClick={() => setOpen(false)}
              className="flex items-center gap-2 px-3 py-2 text-xs text-pelip-text-soft hover:bg-pelip-bg-elev-2"
            >
              <Inbox className="w-3.5 h-3.5 text-amber-600 dark:text-amber-400" />
              Buzón de feedback
            </Link>
          )}
          {isOwner && (
            <Link
              to="/admin/correcciones"
              onClick={() => setOpen(false)}
              className="flex items-center gap-2 px-3 py-2 text-xs text-pelip-text-soft hover:bg-pelip-bg-elev-2"
            >
              <BarChart2 className="w-3.5 h-3.5 text-amber-600 dark:text-amber-400" />
              Correcciones del presupuesto
            </Link>
          )}
          {isStaff && (
            <Link
              to="/admin/parametros-legales"
              onClick={() => setOpen(false)}
              className="flex items-center gap-2 px-3 py-2 text-xs text-pelip-text-soft hover:bg-pelip-bg-elev-2"
            >
              <Scale className="w-3.5 h-3.5 text-amber-600 dark:text-amber-400" />
              Parámetros legales
            </Link>
          )}

          <button
            onClick={() => {
              setOpen(false);
              navigate('/');
            }}
            className="w-full flex items-center gap-2 px-3 py-2 text-xs text-pelip-text-soft hover:bg-pelip-bg-elev-2"
          >
            <User className="w-3.5 h-3.5 text-pelip-text-muted" />
            Mi cuenta
          </button>

          <Link
            to="/auth/actualizar-clave"
            onClick={() => setOpen(false)}
            className="w-full flex items-center gap-2 px-3 py-2 text-xs text-pelip-text-soft hover:bg-pelip-bg-elev-2"
          >
            <KeyRound className="w-3.5 h-3.5 text-pelip-text-muted" />
            Cambiar contraseña
          </Link>

          <button
            onClick={async () => {
              setOpen(false);
              await signOut();
              navigate('/login');
            }}
            className="w-full flex items-center gap-2 px-3 py-2 text-xs text-pelip-text-soft hover:bg-red-950/40 hover:text-red-300 border-t border-pelip-border"
          >
            <LogOut className="w-3.5 h-3.5" />
            Cerrar sesión
          </button>

          {isOwner && (
            <div className="px-3 py-1.5 text-[9px] text-pelip-text-faint uppercase tracking-wider border-t border-pelip-border flex items-center gap-1">
              <ShieldCheck className="w-3 h-3" />
              Modo administrador
            </div>
          )}
        </div>
      )}
    </div>
  );
}
